import React from 'react';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import { MenuItem } from '../components/Menu';
import SVGIcon from '../components/SVGIcon';
import Tooltip from '../components/Tooltip';
import { SidebarItem, UserType } from '../graphql/generated/graphql';
import addIcon from '../icons/non-prefilled/16x16/add.svg';
import reorderIcon from '../icons/non-prefilled/16x16/reorder.svg';
import NewTopicModal from '../topic/modal/NewTopicModal';
import type { AllTopicsTreeListTopic } from '../topic/useAllTopics';
import { isFeatureEnabled } from '../utils/features';
import EditSidebarSectionMenuItem from './EditSidebarSectionMenuItem';
import ReorderMyTopicsModal from './ReorderMyTopicsModal';
import ReorderTopicsModal from './ReorderTopicsModal';
import SidebarItems from './SidebarItems';
import SidebarSection from './SidebarSection';
import SidebarSectionIcon from './SidebarSectionIcon';
import { PremadeSections } from './SidebarSectionsProvider';
import styles from './SidebarTopics.module.scss';
import sortSidebarItems from './sortSidebarItems';
import useSidebarSettings from './useSidebarSettings';
import { useIsSize } from '../utils/responsive/isSize';
import Sizes from '../utils/responsive/Sizes';
import addTestId from '../utils/addTestId';
import { UIActions } from '../ui/uiSlice';

const CreateTopicButton = () => {
  const currentUser = useAppSelector((state) => state.session.user);
  const isGuest = currentUser.type === UserType.Guest;
  const dispatch = useAppDispatch();

  if (isGuest) return null;

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions -- This button will be removed with new Sidebar sections
    <div
      className={styles.addWrapper}
      onClick={(e) => {
        e.stopPropagation();
        dispatch(UIActions.openModal(NewTopicModal));
      }}
    >
      <Tooltip
        placement="top"
        popOverPortal={document.body}
        tooltip="Create a new topic"
        trigger="hover"
      >
        <div {...addTestId('addTopicButton')} className={styles.add}>
          <SVGIcon size={9}>{addIcon}</SVGIcon>
        </div>
      </Tooltip>
    </div>
  );
};

interface SidebarMyTopicsProps {
  hidden?: boolean;
  myTopics: AllTopicsTreeListTopic[];
  myTopicIds: string[];
  activeTopics: Record<string, boolean>;
  expandedTopics: Record<string, boolean>;
}

export const SidebarMyTopics = ({
  hidden,
  myTopics,
  myTopicIds,
  activeTopics,
  expandedTopics,
}: SidebarMyTopicsProps) => {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((state) => state.session.user);
  const isGuest = currentUser.type === UserType.Guest;
  const isMobile = useIsSize(Sizes.SM);
  if (isGuest) return null;

  const menuItems = (() => {
    if (isFeatureEnabled('sidebar_section')) {
      if (isMobile) return undefined;

      return (
        <EditSidebarSectionMenuItem sectionId={PremadeSections.MY_TOPICS} />
      );
    }

    return (
      <MenuItem
        icon={reorderIcon}
        onClick={() => {
          dispatch(
            UIActions.openModal(ReorderMyTopicsModal, {
              myTopics: sortSidebarItems(myTopics, myTopicIds),
            }),
          );
        }}
      >
        Reorder topics
      </MenuItem>
    );
  })();

  return (
    <SidebarSection
      id="myTopics"
      icon={<SidebarSectionIcon section={{ id: PremadeSections.MY_TOPICS }} />}
      title="My Topics"
      hidden={hidden}
      menuItems={menuItems}
      customMenu={<CreateTopicButton />}
    >
      <SidebarItems
        topics={myTopics}
        orderIds={myTopicIds}
        activeTopics={activeTopics}
        expandedTopics={expandedTopics}
      />
    </SidebarSection>
  );
};

interface SidebarAllTopicsProps {
  hidden?: boolean;
  allTopics: AllTopicsTreeListTopic[];
  activeTopics: Record<string, boolean>;
  expandedTopics: Record<string, boolean>;
}

export const SidebarAllTopics = ({
  hidden,
  allTopics,
  activeTopics,
  expandedTopics,
}: SidebarAllTopicsProps) => {
  const isMobile = useIsSize(Sizes.SM);
  const dispatch = useAppDispatch();

  const menuItems = (() => {
    if (isFeatureEnabled('sidebar_section')) {
      if (isMobile) return undefined;

      return (
        <EditSidebarSectionMenuItem sectionId={PremadeSections.ALL_TOPICS} />
      );
    }

    return (
      <MenuItem
        icon={reorderIcon}
        onClick={() => {
          dispatch(
            UIActions.openModal(ReorderTopicsModal, {
              topics: allTopics,
            }),
          );
        }}
      >
        Reorder topics
      </MenuItem>
    );
  })();

  return (
    <SidebarSection
      id="allTopics"
      title="All Topics"
      icon={<SidebarSectionIcon section={{ id: PremadeSections.ALL_TOPICS }} />}
      hidden={hidden}
      menuItems={menuItems}
      customMenu={<CreateTopicButton />}
      empty={allTopics.length === 0}
    >
      <SidebarItems
        topics={allTopics}
        activeTopics={activeTopics}
        expandedTopics={expandedTopics}
      />
    </SidebarSection>
  );
};

interface SidebarTopicsProps {
  activeTopics: Record<string, boolean>;
  expandedTopics: Record<string, boolean>;
  myTopics: AllTopicsTreeListTopic[] | null;
  allTopics: AllTopicsTreeListTopic[];
}

const SidebarTopics = ({
  activeTopics,
  expandedTopics,
  myTopics,
  allTopics,
}: SidebarTopicsProps) => {
  const { hiddenItems, myTopicIds } = useSidebarSettings();

  return (
    <>
      {myTopics && myTopics.length > 0 && (
        <SidebarMyTopics
          myTopicIds={myTopicIds}
          myTopics={myTopics}
          activeTopics={activeTopics}
          expandedTopics={expandedTopics}
          hidden={hiddenItems.has(SidebarItem.MyTopics)}
        />
      )}
      <SidebarAllTopics
        hidden={hiddenItems.has(SidebarItem.AllTopics)}
        allTopics={allTopics}
        activeTopics={activeTopics}
        expandedTopics={expandedTopics}
      />
    </>
  );
};

export default SidebarTopics;
